import React, { createContext, useContext, useRef, ReactNode, useEffect, useCallback } from 'react';

import { usePredictionsInfoContext } from '../context/predictions-info-context';
import { CELL_WIDTH } from '../../../core/constants';

interface DataGridScrollContextType {
  scrollLeftRef: React.MutableRefObject<number>;
  updateScrollPosition: (isNewViewCashFlow: boolean) => void;
  resetScrollPosition: () => void;
}

const DataGridScrollContext = createContext<DataGridScrollContextType | null>(null);

export const SCROLL_INITIAL_VALUE = -1;

const INITIAL_SCROLL_OFFSET = 1;

export const getInitialScrollPosition = (totalPastDates: number) =>
  (totalPastDates - INITIAL_SCROLL_OFFSET) * CELL_WIDTH;

export const DataGridScrollProvider = ({ children }: { children: ReactNode }) => {
  const { totalPastDates, forecast } = usePredictionsInfoContext();
  const scrollLeftRef = useRef(SCROLL_INITIAL_VALUE);

  useEffect(() => {
    if (typeof totalPastDates === 'number') {
      scrollLeftRef.current = getInitialScrollPosition(totalPastDates);
    }
  }, [totalPastDates, forecast?.id]);

  const updateScrollPosition = useCallback(
    (isNewViewCashFlow: boolean) => {
      // We don't have values in the past for cashflow view so scroll value needs to be 0
      if (isNewViewCashFlow) {
        scrollLeftRef.current = SCROLL_INITIAL_VALUE;
        return;
      }
      // When changing Cash flow view to accruals, we need to use totalPastDates to calculate default scroll position
      if (typeof totalPastDates === 'number') {
        scrollLeftRef.current = getInitialScrollPosition(totalPastDates);
      } else {
        scrollLeftRef.current = SCROLL_INITIAL_VALUE;
      }
    },
    [totalPastDates]
  );

  const resetScrollPosition = useCallback(() => {
    scrollLeftRef.current = SCROLL_INITIAL_VALUE;
  }, []);

  return (
    <DataGridScrollContext.Provider value={{ scrollLeftRef, updateScrollPosition, resetScrollPosition }}>
      {children}
    </DataGridScrollContext.Provider>
  );
};

export const useDataGridScrollContext = (): DataGridScrollContextType => {
  const context = useContext(DataGridScrollContext);
  if (context === null) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }
  return context;
};
